import { useEffect, useState } from "react";
import { Elements, useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { Appearance, loadStripe, Stripe } from "@stripe/stripe-js";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import toast from "react-hot-toast";
import { slackHook } from "src/utils/slack-hook";
import { useAuth } from "src/hooks/use-auth";

export interface StripeSetupElementProps {
  setupIntentClientSecret: string | null;
  stripeConnectAccountId: string | null;
  handleSetupSuccess: (payment_method_id: string) => void;
  onCancel?: () => void;
  buttonText?: string;
}

// Inner component that uses Stripe hooks
function StripeSetupForm({ handleSetupSuccess, onCancel, buttonText: initialButtonText = "Start free trial" }: {
  handleSetupSuccess: (payment_method_id: string) => void;
  onCancel?: () => void;
  buttonText?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [attemptingSetup, setAttemptingSetup] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [buttonText, setButtonText] = useState<string>(initialButtonText);

  const handleSubmit = async () => {
    setError("");
    if (!stripe || !elements) {
      setError(
        `Oh no! We're having an issue loading some data. Wait a few moments then give it another go.`
      );
      return;
    }

    setAttemptingSetup(true);
    setButtonText("Processing...");

    const returnUrl = window.location.href;

    // Use confirmSetup for SetupIntent (not confirmPayment)
    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (error) {
      setError(error.message || "Setup failed. Please check your payment details and try again.");
      slackHook(`Setup Error: ${error.message}`);
      setButtonText(initialButtonText);
      setAttemptingSetup(false);
      return;
    }

    const status = setupIntent?.status;
    // Extract payment method ID from SetupIntent
    const paymentMethodId = typeof setupIntent?.payment_method === 'string'
      ? setupIntent.payment_method
      : (setupIntent?.payment_method as any)?.id;

    if (status === "succeeded" && paymentMethodId) {
      toast.success("Payment method saved successfully!");
      slackHook(
        `${window.location.hostname}: SETUP SUCCESS for ${user?.username}. Payment method: ${paymentMethodId}`
      );
      setButtonText("Success!");
      handleSetupSuccess(paymentMethodId);
    } else {
      setError("Failed to save payment method. Please try again.");
      setButtonText(initialButtonText);
      setAttemptingSetup(false);
    }
  };

  return (
    <Stack spacing={3}>
      <PaymentElement />

      {error && (
        <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>
          {error}
        </Box>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={attemptingSetup}
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!stripe || attemptingSetup}
          size="large"
        >
          {buttonText}
        </Button>
      </Stack>

      {/* <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        <LockRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        <SeverityPillWrap
          color="info"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 400,
            backgroundColor: "transparent",
            color: "text.secondary",
          }}
        >
          Secured by Stripe
        </SeverityPillWrap>
      </Stack> */}
    </Stack>
  );
}

export function StripeSetupElement(props: StripeSetupElementProps) {
  const {
    setupIntentClientSecret,
    stripeConnectAccountId,
    handleSetupSuccess,
    onCancel,
    buttonText,
  } = props;

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // Initialize Stripe with the publishable key and connected account
  useEffect(() => {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

    // Create Stripe promise with or without Connect account
    const promise = stripeConnectAccountId
      ? loadStripe(publishableKey, {
          stripeAccount: stripeConnectAccountId,
        })
      : loadStripe(publishableKey);

    setStripePromise(promise);
  }, [stripeConnectAccountId]);

  // Don't render Elements until we have both the client secret AND Stripe is initialized
  if (!setupIntentClientSecret || !stripePromise) {
    return null;
  }

  const appearance = {
    variables: {
      fontFamily: ' "Gill Sans", sans-serif',
      fontLineHeight: "1.5",
      borderRadius: "10px",
      colorBackground: "#F6F8FA",
      colorPrimaryText: "#262626",
    },
    rules: {
      ".Block": {
        backgroundColor: "var(--colorBackground)",
        boxShadow: "none",
        padding: "12px",
      },
      ".Input": {
        padding: "12px",
      },
      ".Input:disabled, .Input--invalid:disabled": {
        color: "lightgray",
      },
      ".Tab": {
        padding: "10px 12px 8px 12px",
        border: "none",
      },
      ".Tab:hover": {
        border: "none",
        boxShadow:
          "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      },
      ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
        border: "none",
        backgroundColor: "#fff",
        boxShadow:
          "0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)",
      },
      ".Label": {
        fontWeight: "500",
      },
    },
  } as Appearance;

  return (
    <>
      {setupIntentClientSecret && stripePromise && (
        <Elements
          key={setupIntentClientSecret}
          stripe={stripePromise}
          options={{
            clientSecret: setupIntentClientSecret,
            loader: "auto",
            appearance: appearance,
          }}
        >
          <StripeSetupForm
            handleSetupSuccess={handleSetupSuccess}
            onCancel={onCancel}
            buttonText={buttonText}
          />
        </Elements>
      )}
    </>
  );
}
